import { PlatformLogoutReason } from '../../models/enums/general-enums';
import { Container } from 'typedi';
import { SessionTimerInterval, UserContextToken, WindowToken } from '../../injection-tokens';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@unified-client/event-formatter';
import {
    IIdentityChannel,
    ILogoutData,
    ISessionChannel,
    ITopicEnvelope,
    MessageBroker,
    IHeartBeat,
    ILogger,
} from '@sparkware/uc-sdk-core';
import { LoggerProvider } from '../logger';
import { AuthenticationFlowType } from '../authentication/enums';
import { ClickStreamTrackingProvider } from '../tracking';
import SessionApi from '../../../APIs/SessionApi';
import { IUserContext } from '../user-context/user-context-interface';
import { UserareaService } from '../external/userarea';
import { Utils } from '../utils';
import {
    ClientsFrameworkInitService,
    ClientsFrameworkLogoutService,
} from '../external/clients-framework';
import { IMrGreenMigration } from '../mr-green-migration/models/interfaces/IMrGreenMigration';
import { IAuthenticationFlowFactory, IAuthenticationFlowRunner } from '../authentication/models';
import { ISessionUserData } from './interfaces/ISessionUserData';
import { IGeolocation } from '../geolocation/models/IGeolocation';
import { ISessionManager } from './interfaces/ISessionManager';
import { LoaderManager } from '../../loaders/LoaderManager';

export abstract class BaseSessionManager implements ISessionManager {
    private _timerId: number;
    private _lastInteractionTime: number;
    private _crossLoginFlow?: IAuthenticationFlowRunner;
    private readonly _cfInitService: ClientsFrameworkInitService;
    private readonly _cfLogoutService: ClientsFrameworkLogoutService;
    private readonly _channel: ISessionChannel;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _identityChannel: IIdentityChannel;
    private readonly _logger: ILogger;
    private readonly _messagePublisher: string = 'UCF.SDK.SessionManager';
    private readonly _timerIntervalInMilliseconds: number;
    private readonly _userContext: IUserContext;
    private readonly _userareaService: UserareaService;
    private readonly _utilsService: Utils;
    private readonly _window: Window;

    private get _geolocationPromise(): Promise<IGeolocation> {
        return LoaderManager.Instance.GeolocationLoader.Instance;
    }

    get timerId() {
        return this._timerId;
    }

    private get _mrGreenMigrationPromise(): Promise<IMrGreenMigration> {
        return LoaderManager.Instance.MrGreenMigrationLoader.Instance;
    }

    private get _authenticationFlowFactoryPromise(): Promise<IAuthenticationFlowFactory> {
        return LoaderManager.Instance.AuthenticationFlowFactoryLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('SessionManager');
        this._window = Container.get(WindowToken);
        this._userContext = Container.get(UserContextToken);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('SessionManager');
        this._channel = MessageBroker.getInstance().session;
        this._timerIntervalInMilliseconds = Container.get(SessionTimerInterval);
        this._identityChannel = MessageBroker.getInstance().identity;
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._userareaService = Container.get(UserareaService);
        this._cfLogoutService = Container.get(ClientsFrameworkLogoutService);
        this._cfInitService = Container.get(ClientsFrameworkInitService);
        this._utilsService = Container.get(Utils);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
    }

    public onUserInteraction = (): void => {
        if (this._userContext.IsAuthenticated) {
            this._lastInteractionTime = Date.now();
        }
    };

    public onLogoutMessage = (message: ILogoutData, envelope: ITopicEnvelope) => {
        const { headers } = envelope;

        // Only run doLogout when logout is initiated by hosted client
        if (headers.publisher !== 'client-integration' && headers.publisher !== 'pokerWebClient') {
            this._cfLogoutService.doLogout(message.Reason);
        }
        this._clearTimerId();
    };

    public onInactivityTimeoutMessage = () => {
        this._sendClientStartedLogoutProcessEvent();
        this._clearTimerId();
        if (window.navigator.onLine === true) {
            this._cfLogoutService.doLogoutAndShowTimeoutDialog(
                PlatformLogoutReason.ClientDisconnected,
            );
        }
    };

    public onHeartbeatMessage = (data?: any) => {
        if (!!data?.updateUserInteraction) {
            this.onUserInteraction();
        }
    };

    public onLogoutSuccess = () => {
        this._clearTimerId();
    };

    protected _onDataStoreReady = async (data: ISessionUserData) => {
        const mrGreenMigration = await this._mrGreenMigrationPromise;
        if (mrGreenMigration) {
            await mrGreenMigration.SendBiVisitEvent();
        }
        if (this._userContext.IsAuthenticated) {
            const geolocation = await this._geolocationPromise;
            await geolocation?.initFlowAfterRefresh();

            this._registerRefreshUserStateUserarea(data);
        }
    };

    protected _tryAutoLogin = async () => {
        this._logger.debug(`[_tryAutoLogin] started`);
        const authenticationFlowFactory = await this._authenticationFlowFactoryPromise;
        this._crossLoginFlow = await authenticationFlowFactory.create(AuthenticationFlowType.Cross);
        await this._crossLoginFlow.execute();
        this._logger.debug(`[_tryAutoLogin] end`);
    };

    protected _setTimerId = () => {
        this._timerId = this._window.setInterval(
            this._onInterval,
            this._timerIntervalInMilliseconds,
        );
    };

    private _sendClientStartedLogoutProcessEvent = () => {
        const formatter = this._eventFormatterBuilder.createFormatter(
            '_sendClientStartedLogoutProcessEvent',
        );
        const elasticEventObj = formatter.formatUCEvent(
            { message: 'Customer logout request by the offering client.' },
            { correlationID: this._utilsService.generateCorrelationID() },
            { event: 'Client logout request' },
        );

        this._clickStreamTrackingProvider.sendEventV2(elasticEventObj);
    };

    private _clearTimerId = () => {
        if (this.timerId) clearInterval(this.timerId);
    };

    private _onInterval = () => {
        let timePassedSinceLastInteraction = Date.now() - this._lastInteractionTime;
        this._logger.debug(
            `onInterval. timePassedSinceLastInteraction: ${
                timePassedSinceLastInteraction / 1000
            } seconds`,
        );
        if (timePassedSinceLastInteraction <= this._timerIntervalInMilliseconds) {
            this._sendHeartBeat();
        }
    };

    private _sendHeartBeat = (): void => {
        this._logger.debug(
            `sendHeartBeat. Sending heartbeat to Session Management System at : ${new Date().toLocaleString()}`,
        );
        SessionApi.HeartBeat();
        this._channel.topics.heartBeat.publish(
            {
                publisher: 'ucf.sdk',
            },
            {} as IHeartBeat,
        );
    };

    private _registerRefreshUserStateUserarea(userStateData): void {
        this._userareaService.executeOnload(() => {
            this._publishUserStateRefreshLogin(userStateData);
            this._cfInitService.RealityCheck();
        });
    }

    private _publishUserStateRefreshLogin(userStateData: ISessionUserData): void {
        this._identityChannel.topics.loginSuccess.publish(
            { publisher: this._messagePublisher },
            {
                InitialState: userStateData.StoreState,
                AuthenticateData: userStateData.AuthenticateData,
            },
        );
    }
}
